/**
 * A preset: one named bundle of picks across the layers. The engine consumes this shape; presets
 * themselves (and their role, surface and consumer metadata) are data authored elsewhere.
 *
 * Several presets may read one judged take: sight reading, for instance, runs a clearance
 * preset and a controller preset over the same note judgments, diverging at combining.
 */

import {
  CombiningConfig,
  ViewScores,
  combiningErrors,
  scoreViews,
  withOutermostStatistic,
} from './combining';
import { JudgmentConfig, judgmentConfigErrors } from './grader';
import { JudgedTake } from './judgedTake';
import { AccumulatorConfig, accumulatorErrors } from './policy';
import { GradingConfigError } from './types';
import {
  StatisticScores,
  Verdict,
  VerdictRule,
  judgeVerdicts,
  statisticsRead,
  verdictErrors,
} from './verdict';

/** Layers 2 and 3: how a judged take becomes scores and labels. */
export interface ScoringConfig {
  readonly combining: CombiningConfig;
  /** Absent: the scores are only shown (zero cut lines). */
  readonly verdict?: VerdictRule;
}

export interface GradingPreset {
  readonly name: string;
  /** Layers 0 to 1.5. */
  readonly judgment: JudgmentConfig;
  /** Layers 2 and 3. */
  readonly scoring: ScoringConfig;
  /** Layer 4: the accumulators the preset's labels feed, by name. */
  readonly policy: Readonly<Record<string, AccumulatorConfig>>;
}

/** Problems with layers 2 and 3, including each statistic the verdict reads in another's place. */
export function scoringErrors(scoring: ScoringConfig): string[] {
  const readErrors = scoring.verdict
    ? statisticsRead(scoring.verdict).reduce<string[]>(
        (errors, statistic) => [
          ...errors,
          ...combiningErrors(withOutermostStatistic(scoring.combining, statistic)).map(
            (error) => `read by ${statistic}: ${error}`,
          ),
        ],
        [],
      )
    : [];
  return [
    ...combiningErrors(scoring.combining),
    ...(scoring.verdict ? verdictErrors(scoring.verdict) : []),
    ...readErrors,
  ];
}

export function presetErrors(preset: GradingPreset): string[] {
  const policyErrors = Object.keys(preset.policy).reduce<string[]>(
    (errors, name) => [
      ...errors,
      ...accumulatorErrors(preset.policy[name]).map((error) => `${name}: ${error}`),
    ],
    [],
  );
  return [
    ...judgmentConfigErrors(preset.judgment),
    ...scoringErrors(preset.scoring),
    ...policyErrors,
  ];
}

export function validatePreset(preset: GradingPreset): void {
  const errors = presetErrors(preset);
  if (errors.length > 0) {
    throw new GradingConfigError(`preset ${preset.name}: ${errors.join('; ')}`);
  }
}

export interface TakeScore {
  /** Combined, left-hand and right-hand scores, always all three. */
  readonly views: ViewScores;
  /** One verdict per bounded group (or one on the running value); empty without a verdict. */
  readonly verdicts: readonly Verdict[];
}

/** Layers 2 and 3 over a judged take. */
export function scoreTake(take: JudgedTake, scoring: ScoringConfig): TakeScore {
  const views = scoreViews(take, scoring.combining);
  if (!scoring.verdict) {
    return { views, verdicts: [] };
  }
  const byStatistic = statisticsRead(scoring.verdict).reduce<StatisticScores>(
    (found, statistic) => ({
      ...found,
      [statistic]: scoreViews(take, withOutermostStatistic(scoring.combining, statistic)),
    }),
    {},
  );
  return {
    views,
    verdicts: judgeVerdicts(scoring.verdict, views, take, byStatistic),
  };
}
